import PaginationFragment from "../../../../components/fragment/pagination/PaginationFragment";
import { CurrencyPairRate, isExclusiveRate, PROVIDER_LABEL, RateProvider } from "../types";
import PositionBadge from "./PositionBadge";

function formatRate(rate: number): string {
  // Reverse pairs (e.g. NGN → CAD) carry sub-unit rates like 0.0010111121 —
  // a fixed 2-decimal format would round every one of those to "0.00", so
  // small rates instead keep a fixed number of significant digits.
  if (Math.abs(rate) < 1) {
    return rate.toLocaleString("en-US", {
      minimumSignificantDigits: 3,
      maximumSignificantDigits: 4,
    });
  }

  return rate.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

interface RateComparisonTableProps {
  pairs: CurrencyPairRate[];
  providers: RateProvider[];
  quotedAt?: string;
  loading?: boolean;
  currentPage?: number;
  perPage?: number;
  totalCount?: number;
  onPageChange?: (page: number) => void;
}

export default function RateComparisonTable({
  pairs,
  providers,
  quotedAt,
  loading,
  currentPage,
  perPage,
  totalCount,
  onPageChange,
}: RateComparisonTableProps) {
  const columnCount = providers.length + 2;

  return (
    <div className="bg-white border border-[#E4E4E7] rounded-[8px] overflow-hidden">
      <div className="flex items-center justify-between px-[20px] py-[12px] border-b border-[#E4E4E7]">
        <p className="text-[13px] text-[#1E1E1E]">
          Latest quotes · {quotedAt || "—"}
        </p>
        <p className="text-[12px] text-[#A1A1AA]">Underlined = best fresh rate</p>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-[#F8F9FF] text-[13px] font-medium uppercase text-[#191C1F]">
              <th className="px-[20px] py-[12px] whitespace-nowrap">Currency pair</th>
              {providers.map((provider) => (
                <th
                  key={provider}
                  className={`px-[16px] py-[12px] whitespace-nowrap ${
                    provider === "smartsend" ? "bg-[#F4F0FB] text-[#734792]" : ""
                  }`}
                >
                  {PROVIDER_LABEL[provider]}
                </th>
              ))}
              <th className="px-[20px] py-[12px] whitespace-nowrap">Position</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td
                  colSpan={columnCount}
                  className="px-[20px] py-[32px] text-center text-[13px] text-[#A1A1AA]"
                >
                  Loading rates…
                </td>
              </tr>
            ) : pairs.length < 1 ? (
              <tr>
                <td
                  colSpan={columnCount}
                  className="px-[20px] py-[32px] text-center text-[13px] text-[#A1A1AA]"
                >
                  No currency pairs match these filters
                </td>
              </tr>
            ) : (
              pairs.map((pair) => (
                <tr key={pair.id} className="border-t border-[#F0F0F0]">
                  <td className="px-[20px] py-[16px] text-[14px] font-medium text-[#1E1E1E] whitespace-nowrap">
                    {pair.pair_label}
                  </td>
                  {providers.map((provider) => {
                    const q = pair.quotes[provider];
                    const isSmartSend = provider === "smartsend";
                    return (
                      <td
                        key={provider}
                        className={`px-[16px] py-[16px] text-[14px] whitespace-nowrap ${
                          isSmartSend ? "bg-[#F9F6FC]" : ""
                        }`}
                      >
                        {q.rate === null ? (
                          <>
                            <p className="text-[#1E1E1E]">—</p>
                            <p className="text-[12px] text-[#A1A1AA]">No quote</p>
                          </>
                        ) : (
                          <>
                            <p
                              className={[
                                isSmartSend
                                  ? "font-bold text-[#734792]"
                                  : q.is_best
                                    ? "font-bold text-[#1E1E1E]"
                                    : "text-[#1E1E1E]",
                                q.is_best ? "underline" : "",
                              ].join(" ")}
                            >
                              {formatRate(q.rate)}
                            </p>
                            <p className="text-[12px] text-[#A1A1AA]">
                              {q.is_stale
                                ? `Stale · ${q.updated_minutes_ago}m`
                                : `${q.updated_minutes_ago}m ago`}
                            </p>
                          </>
                        )}
                      </td>
                    );
                  })}
                  <td className="px-[20px] py-[16px]">
                    <PositionBadge
                      position={pair.position}
                      percent={pair.position_percent}
                      isExclusive={isExclusiveRate(pair)}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="border-t border-[#E4E4E7]">
        <PaginationFragment
          currentPage={currentPage}
          perPage={perPage}
          totalPage={totalCount || 0}
          hidePagination={loading}
          onPageChange={onPageChange}
        />
      </div>
    </div>
  );
}
